import { orgNatsClient, StatementIds } from './OrgNATSClient';

/**
 * Unit conversion configuration for packaging lines.
 * Allows calculating meaningful KPIs when machines handle different units.
 */
export interface UnitConversion {
  /** Name of the unit at this stage (e.g., "packets", "boxes", "racks") */
  unitName: string;
  /** How many of the previous unit fit in this unit (e.g., 10 packets per box) */
  unitsPerContainer: number;
}

/**
 * Configuration for a machine's position in the line KPI calculation.
 */
export interface MachineKPIConfig {
  machineId: string;
  /** Unit conversion from previous machine (if applicable) */
  unitConversion?: UnitConversion;
  /** Custom weight for aggregating this machine's KPIs (0-1, default 1) */
  weight?: number;
}

/**
 * Complete configuration for line-level KPI calculation.
 */
export interface LineKPIConfiguration {
  lineId: string;
  /** Configuration per machine in the line */
  machines: MachineKPIConfig[];
  /** Global line targets (can override machine-type targets) */
  targets?: {
    efficiency?: number;      // percentage (0-100)
    utilization?: number;     // percentage (0-100)
    rejectRatio?: number;     // percentage (0-100)
    outputPerShift?: number;  // units per shift
  };
  /** Description of the line's output unit (e.g., "racks of 24 boxes") */
  outputUnitDescription?: string;
  /** ISO timestamp of last update */
  updatedAt: string;
}

/**
 * Save line KPI configuration to backend via NATS.
 */
export async function saveLineKPIConfig(
  lineId: string,
  config: Omit<LineKPIConfiguration, 'lineId' | 'updatedAt'>,
  token: string
): Promise<boolean> {
  const response = await orgNatsClient.request(
    StatementIds.SAVE_LINE_KPI_CONFIG,
    {
      line_id: lineId,
      machines: config.machines.map(m => ({
        machine_id: m.machineId,
        unit_name: m.unitConversion?.unitName,
        units_per_container: m.unitConversion?.unitsPerContainer,
        weight: m.weight,
      })),
      // Flat target fields to match backend schema
      target_efficiency: config.targets?.efficiency,
      target_output: config.targets?.outputPerShift,
      target_reject_ratio: config.targets?.rejectRatio,
      output_unit_description: config.outputUnitDescription,
    },
    token,
    { service: 'cockpitservice' }
  );
  
  if (!response.success) {
    console.error('Failed to save line KPI config:', response.error);
    return false;
  }
  
  return true;
}

/**
 * Load line KPI configuration from backend via NATS.
 */
export async function getLineKPIConfig(
  lineId: string,
  token: string
): Promise<LineKPIConfiguration | null> {
  const response = await orgNatsClient.request(
    StatementIds.GET_LINE_KPI_CONFIG,
    { line_id: lineId },
    token,
    { service: 'data' }  // Use data.request for GET operations
  );
  
  console.log('📡 getLineKPIConfig response:', response);
  
  if (!response.success || !response.data) {
    console.log('📡 getLineKPIConfig: No data or not successful');
    return null;
  }
  
  // Handle various response formats:
  // - Direct object: { line_id, target_efficiency, ... }
  // - Array: [{ line_id, target_efficiency, ... }]
  // - Nested in results: { results: [{ line_id, ... }] }
  let data = response.data;
  
  // Check if data is wrapped in results
  if (data.results) {
    data = Array.isArray(data.results) ? data.results[0] : data.results;
  }
  // Check if data is an array
  if (Array.isArray(data)) {
    data = data[0];
  }
  
  if (!data) {
    console.log('📡 getLineKPIConfig: Data is empty after unwrapping');
    return null;
  }
  
  console.log('📡 getLineKPIConfig parsed data:', data);
  
  // Backend returns flat structure:
  // { line_id, target_efficiency, target_output, target_reject_ratio, output_unit_description, ... }
  return {
    lineId: data.line_id || lineId,
    machines: (data.machines || []).map((m: any) => ({
      machineId: m.machine_id,
      unitConversion: m.unit_name ? {
        unitName: m.unit_name,
        unitsPerContainer: m.units_per_container || 1,
      } : undefined,
      weight: m.weight,
    })),
    targets: {
      efficiency: data.target_efficiency ?? undefined,
      outputPerShift: data.target_output ?? undefined,
      rejectRatio: data.target_reject_ratio ?? undefined,
    },
    outputUnitDescription: data.output_unit_description ?? undefined,
    updatedAt: data.updated_at || new Date().toISOString(),
  };
}

/**
 * Calculate effective output units considering unit conversions.
 * Uses the last machine in the config (from flow order) as the output reference.
 */
export function calculateEffectiveUnits(
  config: LineKPIConfiguration | null,
  outputUnits: number,
  targetUnit?: string
): { value: number; unit: string } {
  if (!config || config.machines.length === 0) {
    return { value: outputUnits, unit: 'units' };
  }
  
  // Last machine in the flow order is the output reference
  const outputMachineConfig = config.machines[config.machines.length - 1];
  if (!outputMachineConfig) {
    return { value: outputUnits, unit: 'units' };
  }
  
  const outputUnit = outputMachineConfig.unitConversion?.unitName || 'units';
  
  if (!targetUnit || targetUnit === outputUnit) {
    return { value: outputUnits, unit: outputUnit };
  }
  
  // Calculate conversion chain
  const conversionChain: { unit: string; multiplier: number }[] = [];
  
  for (const machineConfig of config.machines) {
    if (machineConfig.unitConversion) {
      conversionChain.push({
        unit: machineConfig.unitConversion.unitName,
        multiplier: machineConfig.unitConversion.unitsPerContainer,
      });
    }
  }
  
  let totalMultiplier = 1;
  let foundOutput = false;
  let foundTarget = false;
  
  for (const step of conversionChain) {
    if (step.unit === outputUnit) {
      foundOutput = true;
    }
    if (foundOutput && !foundTarget) {
      totalMultiplier *= step.multiplier;
    }
    if (step.unit === targetUnit) {
      foundTarget = true;
      break;
    }
  }
  
  if (!foundTarget) {
    return { value: outputUnits, unit: outputUnit };
  }
  
  return { value: outputUnits * totalMultiplier, unit: targetUnit };
}
